import { useState, useEffect } from 'react';
import { checklistCategories } from '../../../mocks/preparedness';

export default function InteractiveChecklist() {
  const [checkedItems, setCheckedItems] = useState<Record<number, boolean>>({});
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});

  // Load saved progress from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('emergency-checklist');
    if (saved) {
      setCheckedItems(JSON.parse(saved));
    }
    // Expand first category by default
    setExpandedCategories({ [checklistCategories[0].id]: true });
  }, []);

  // Save progress to localStorage
  useEffect(() => {
    localStorage.setItem('emergency-checklist', JSON.stringify(checkedItems));
  }, [checkedItems]);

  const toggleItem = (itemId: number) => {
    setCheckedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId],
    }));
  };

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  const getCategoryProgress = (categoryId: string) => {
    const category = checklistCategories.find(c => c.id === categoryId);
    if (!category) return 0;
    const checkedCount = category.items.filter(item => checkedItems[item.id]).length;
    return Math.round((checkedCount / category.items.length) * 100);
  };

  const getTotalProgress = () => {
    const totalItems = checklistCategories.reduce((sum, cat) => sum + cat.items.length, 0);
    const checkedCount = Object.values(checkedItems).filter(Boolean).length;
    return Math.round((checkedCount / totalItems) * 100);
  };

  const colorClasses: Record<string, { bg: string; text: string; border: string; icon: string }> = {
    blue: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', icon: 'bg-blue-100 text-blue-600' },
    red: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', icon: 'bg-red-100 text-red-600' },
    orange: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200', icon: 'bg-orange-100 text-orange-600' },
    purple: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200', icon: 'bg-purple-100 text-purple-600' },
    teal: { bg: 'bg-teal-50', text: 'text-teal-700', border: 'border-teal-200', icon: 'bg-teal-100 text-teal-600' },
    green: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200', icon: 'bg-green-100 text-green-600' },
    pink: { bg: 'bg-pink-50', text: 'text-pink-700', border: 'border-pink-200', icon: 'bg-pink-100 text-pink-600' },
    indigo: { bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200', icon: 'bg-indigo-100 text-indigo-600' },
    yellow: { bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200', icon: 'bg-yellow-100 text-yellow-600' },
    cyan: { bg: 'bg-cyan-50', text: 'text-cyan-700', border: 'border-cyan-200', icon: 'bg-cyan-100 text-cyan-600' },
  };

  return (
    <div id="checklist" className="bg-gray-50 py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Your Emergency Kit Checklist
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            Check off items as you gather them. Your progress is automatically saved.
          </p>
          
          {/* Overall Progress */}
          <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-3">
              <span className="text-lg font-semibold text-gray-900">Overall Progress</span>
              <span className="text-2xl font-bold text-orange-600">{getTotalProgress()}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
              <div
                className="bg-gradient-to-r from-orange-500 to-red-500 h-full rounded-full transition-all duration-500"
                style={{ width: `${getTotalProgress()}%` }}
              ></div>
            </div>
          </div>
        </div>

        {/* Checklist Categories */}
        <div className="space-y-4">
          {checklistCategories.map((category) => {
            const colors = colorClasses[category.color];
            const progress = getCategoryProgress(category.id);
            const isExpanded = expandedCategories[category.id];

            return (
              <div key={category.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                <button
                  onClick={() => toggleCategory(category.id)}
                  className={`w-full px-6 py-5 flex items-center justify-between hover:bg-gray-50 transition-colors ${colors.bg} border-l-4 ${colors.border}`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-lg ${colors.icon} flex items-center justify-center`}>
                      <i className={`${category.icon} text-2xl`}></i>
                    </div>
                    <div className="text-left">
                      <h3 className={`text-xl font-bold ${colors.text}`}>{category.title}</h3>
                      <p className="text-sm text-gray-600">{category.items.length} items</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <div className={`text-lg font-bold ${colors.text}`}>{progress}%</div>
                      <div className="w-32 bg-gray-200 rounded-full h-2 mt-1">
                        <div
                          className={`${colors.border.replace('border-', 'bg-')} h-full rounded-full transition-all duration-300`}
                          style={{ width: `${progress}%` }}
                        ></div>
                      </div>
                    </div>
                    <i className={`ri-arrow-${isExpanded ? 'up' : 'down'}-s-line text-2xl text-gray-400`}></i>
                  </div>
                </button>

                {isExpanded && (
                  <div className="px-6 py-4 border-t border-gray-200">
                    <div className="space-y-3">
                      {category.items.map((item) => (
                        <label
                          key={item.id}
                          className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors group"
                        >
                          <input
                            type="checkbox"
                            checked={checkedItems[item.id] || false}
                            onChange={() => toggleItem(item.id)}
                            className="mt-1 w-5 h-5 rounded border-gray-300 text-orange-600 focus:ring-orange-500 cursor-pointer"
                          />
                          <span className={`flex-1 text-gray-700 ${checkedItems[item.id] ? 'line-through text-gray-400' : ''}`}>
                            {item.text}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Print Checklist Button */}
        <div className="mt-12 text-center">
          <button
            onClick={() => window.print()}
            className="inline-flex items-center gap-2 bg-orange-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-orange-700 transition-colors shadow-lg hover:shadow-xl whitespace-nowrap"
          >
            <i className="ri-printer-line text-xl"></i>
            Print Checklist
          </button>
        </div>
      </div>
    </div>
  );
}
